import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowLeft, Eye, EyeOff, CheckCircle } from "lucide-react";
import axios from "axios";
import { formatApiError } from "../utils/apiError";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password.length < 10) {
      setError("Password must be at least 10 characters, with upper, lower, number, and symbol");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: password,
      });
      setSuccess(true);
    } catch (err) {
      setError(formatApiError(err, "Failed to reset password. The link may have expired."));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50 flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-slate-600 mb-4">Invalid reset link</p>
          <Link to="/forgot-password">
            <Button>Request New Link</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="px-4 py-4 flex items-center gap-4">
        <Link to="/login" className="text-slate-600 hover:text-slate-900">
          <ArrowLeft className="w-6 h-6" />
        </Link>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-sm">FP</span>
          </div>
        </div>
      </header>

      {/* Form */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 shadow-sm">
            {success ? (
              <div data-testid="reset-password-success" className="text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-emerald-600" />
                </div>
                <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight mb-2">
                  Password Reset!
                </h1>
                <p className="text-slate-600 mb-6">
                  Your password has been successfully reset.
                </p>
                <Link to="/login">
                  <Button
                    data-testid="go-to-login-btn"
                    className="w-full h-12 bg-blue-700 hover:bg-blue-800 text-white font-bold rounded-xl"
                  >
                    Go to Login
                  </Button>
                </Link>
              </div>
            ) : (
              <>
                <h1
                  data-testid="reset-password-title"
                  className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mb-2"
                >
                  Reset Password
                </h1>
                <p className="text-slate-600 mb-6">
                  Enter your new password below.
                </p>

                <form onSubmit={handleSubmit} className="space-y-5">
                  {error && (
                    <div
                      data-testid="reset-password-error"
                      className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm"
                    >
                      {error}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-slate-700 font-medium">
                      New Password
                    </Label>
                    <div className="relative">
                      <Input
                        id="password"
                        data-testid="reset-password-input"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="At least 10 characters"
                        required
                        className="h-12 rounded-xl bg-slate-50 border-slate-200 focus:bg-white pr-12"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className="text-slate-700 font-medium">
                      Confirm Password
                    </Label>
                    <Input
                      id="confirmPassword"
                      data-testid="reset-confirm-password-input"
                      type={showPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Re-enter your password"
                      required
                      className="h-12 rounded-xl bg-slate-50 border-slate-200 focus:bg-white"
                    />
                  </div>

                  <Button
                    type="submit"
                    data-testid="reset-submit-btn"
                    disabled={loading}
                    className="w-full h-12 bg-blue-700 hover:bg-blue-800 text-white font-bold rounded-xl shadow-md shadow-blue-700/20"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                        Resetting...
                      </span>
                    ) : (
                      "Reset Password"
                    )}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
